import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useNotifications } from '../NotificationContext';
import { getConnectionStatus } from '../../api/oauth';
import { goToSignup } from '../../utils/guestSearch';

const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/**
 * Follow this person for LiveNow go-live alerts (entity_live), not a Kick.com follow.
 */
export default function HubFollowButton({ entityId }) {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const {
    isEntitySubscribed,
    getEntitySubscription,
    subscribeToEntity,
    unsubscribe,
  } = useNotifications();

  const [busy, setBusy] = useState(false);
  const [showLoginHint, setShowLoginHint] = useState(false);
  const [showConnectKick, setShowConnectKick] = useState(false);
  const [error, setError] = useState(null);

  const id = String(entityId || '').trim();
  const isFollowed = id ? isEntitySubscribed(id) : false;

  useEffect(() => {
    if (!isLoggedIn || !isFollowed) {
      setShowConnectKick(false);
      return undefined;
    }

    let cancelled = false;
    getConnectionStatus()
      .then((status) => {
        if (!cancelled) setShowConnectKick(!status.kick || status.kickNeedsReauth);
      })
      .catch(() => {
        if (!cancelled) setShowConnectKick(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, isFollowed, id]);

  if (!id) return null;

  const handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setError(null);

    if (!isLoggedIn) {
      setShowLoginHint(true);
      window.setTimeout(() => setShowLoginHint(false), 3000);
      return;
    }

    if (busy) return;
    setBusy(true);
    try {
      if (isFollowed) {
        const row = getEntitySubscription(id);
        if (row?.id) await unsubscribe(row.id);
        setShowConnectKick(false);
      } else {
        await subscribeToEntity(id, ['web']);
      }
    } catch (err) {
      setError(err?.message || (isFollowed ? 'Could not unfollow' : 'Could not follow'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="entity-hub-follow">
      <button
        type="button"
        className={`entity-hub-follow-btn${isFollowed ? ' is-active' : ''}`}
        onClick={handleClick}
        disabled={busy}
        aria-busy={busy}
        aria-pressed={isFollowed}
        aria-label={isFollowed ? 'Unfollow go-live alerts' : 'Follow for go-live alerts'}
        title={isFollowed ? 'Unfollow' : 'Follow for go-live alerts in LiveNow'}
      >
        <BellIcon />
      </button>

      {showLoginHint ? (
        <div className="entity-hub-remind-hint" role="status">
          <span>Log in to follow this creator in LiveNow</span>
          <button
            type="button"
            className="entity-hub-remind-hint-cta"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              goToSignup(navigate);
            }}
          >
            Sign up
          </button>
        </div>
      ) : null}

      {showConnectKick && isFollowed && !showLoginHint ? (
        <div className="entity-hub-remind-hint" role="status">
          <span>Connect Kick in Account to get go-live alerts</span>
          <button
            type="button"
            className="entity-hub-remind-hint-cta"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              navigate('/profile?tab=account');
            }}
          >
            Connect Kick
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="entity-hub-remind-hint" role="alert">
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}
